/**
 * THE RESULT PLATE. What a tap did, drawn rather than listed.
 *
 * Phase 20. A job run used to answer with a headline, a paragraph and two numbers in a side
 * panel, and a crime the same plus Heat; the outcome art the server sends with every response
 * (`jobs.results.*`, `crimes.result.*`) was never drawn, the mastery bar never moved, and a drop
 * the server rolled was never shown. GAME-DESIGN 1.2 calls a step "one tap, an image, 60 to 140
 * words of prose, and a result plate"; this is the plate.
 *
 * Everything on it arrived in the response. The count-up animates a number the server sent; it
 * never computes one. `prefers-reduced-motion` skips the motion and keeps the plate.
 */

import { useEffect, useState } from 'react'
import type { MasteryTier } from '@ashport/shared'
import { MASTERY_THRESHOLD } from '@ashport/shared/formulas'
import { A } from '../art'
import { Icon, type IconName } from './Icon'
import { Art, Meter, Panel, PanelHead, Row } from './Kit'
import { sz } from './viewport'

/** No window (a static render) or a reduced-motion preference: land at once, animate nothing. */
const reduced = () =>
  typeof window === 'undefined' || window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** A number that climbs from zero to `target` over `ms`, or lands at once under reduced motion. */
export function useCountUp(target: number, ms = 700): number {
  const [v, setV] = useState(reduced() ? target : 0)
  useEffect(() => {
    if (reduced()) {
      setV(target)
      return
    }
    let raf = 0
    const t0 = performance.now()
    const tick = (t: number) => {
      const k = Math.min(1, (t - t0) / ms)
      // Ease out, so the last dollars land slowly and read as a total rather than a blur.
      setV(Math.round(target * (1 - (1 - k) * (1 - k))))
      if (k < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, ms])
  return v
}

export interface PlateRow {
  icon: IconName
  label: string
  /** A number to count up, formatted by `fmt`, or a string printed as it is. */
  value: number | string
  fmt?: (n: number) => string
  c: string
  fill?: boolean
}

export interface PlateItem {
  name: string
  qty: number
  artId: string | null
  kind: 'drop' | 'seized'
}

const TIER_C: Record<MasteryTier, string> = {
  none: 'var(--ink-faint)',
  bronze: '#a9743c',
  silver: '#b9c2c8',
  gold: 'var(--gold)',
}

function CountRow({ r }: { r: PlateRow }) {
  const n = useCountUp(typeof r.value === 'number' ? r.value : 0)
  return (
    <Row gap={9} style={{ padding: '5px 0', borderTop: '1px solid var(--line-soft)' }}>
      <Icon n={r.icon} s={13} c={r.c} fill={r.fill} />
      <span style={{ flex: 1, fontSize: sz(11.5), color: 'var(--ink)' }}>{r.label}</span>
      <span className="t-num" style={{ fontSize: sz(13), color: r.c }}>
        {typeof r.value === 'number' ? (r.fmt ?? String)(n) : r.value}
      </span>
    </Row>
  )
}

/**
 * The step's mastery, as a bar with the three tiers marked on it, ticking from the runs before
 * this tap to the runs after. Bronze at 3, Silver at 9, Gold at 19 (`MASTERY_THRESHOLD`).
 */
export function MasteryBar({
  runs,
  from,
  tier,
  compact,
}: {
  runs: number
  /** Where the bar starts its tick. Equal to `runs` for a static bar. */
  from?: number
  tier: MasteryTier
  compact?: boolean
}) {
  const max = MASTERY_THRESHOLD.gold
  const start = from ?? runs
  const shown = Math.min(max, start) + (Math.min(max, runs) - Math.min(max, start)) * (useCountUp(1000, 600) / 1000)
  const marks: { at: number; t: MasteryTier }[] = [
    { at: MASTERY_THRESHOLD.bronze, t: 'bronze' },
    { at: MASTERY_THRESHOLD.silver, t: 'silver' },
    { at: MASTERY_THRESHOLD.gold, t: 'gold' },
  ]
  const next =
    tier === 'gold' ? null : tier === 'silver' ? 'gold' : tier === 'bronze' ? 'silver' : 'bronze'
  const toGo = next ? MASTERY_THRESHOLD[next] - runs : 0
  return (
    <div>
      <div style={{ position: 'relative' }}>
        <Meter v={shown} max={max} a="var(--brass-deep)" b={TIER_C[tier === 'none' ? 'bronze' : tier]} h={compact ? 5 : 7} />
        {marks.map((m) => (
          <i
            key={m.t}
            title={`${m.t} at ${m.at}`}
            style={{
              position: 'absolute',
              left: `${(m.at / max) * 100}%`,
              top: -2,
              bottom: -2,
              width: 2,
              marginLeft: -1,
              background: runs >= m.at ? TIER_C[m.t] : 'var(--line)',
              opacity: runs >= m.at ? 1 : 0.7,
            }}
          />
        ))}
      </div>
      {!compact && (
        <Row gap={8} style={{ marginTop: 4 }}>
          <span className="t-label" style={{ fontSize: sz(8.5), color: TIER_C[tier], letterSpacing: '.12em' }}>
            {tier === 'none' ? `${runs} run${runs === 1 ? '' : 's'}` : tier.toUpperCase()}
          </span>
          <span className="t-mono" style={{ fontSize: sz(9.5), color: 'var(--ink-faint)', marginLeft: 'auto' }}>
            {next ? `${toGo} to ${next}` : 'mastered'}
          </span>
        </Row>
      )}
    </div>
  )
}

export function ResultPlate({
  tone,
  title,
  artId,
  fallbackArtId,
  headline,
  body,
  rows,
  mastery,
  level,
  next,
  items,
  onClose,
}: {
  tone: 'gold' | 'safe' | 'danger'
  title: string
  artId: string
  fallbackArtId: string
  headline: string
  body: string
  rows: PlateRow[]
  mastery?: { runs: number; from: number; before: MasteryTier; after: MasteryTier; stepName: string } | null
  level?: { before: number; after: number; energyMax: number } | null
  next?: string | null
  items?: PlateItem[]
  onClose: () => void
}) {
  const accent = tone === 'gold' ? 'var(--gold)' : tone === 'danger' ? 'var(--arrest)' : 'var(--dirty)'
  const flipped = mastery && mastery.before !== mastery.after
  return (
    <Panel tone={tone}>
      <PanelHead
        title={title}
        icon={tone === 'gold' ? 'legend' : tone === 'danger' ? 'warn' : 'check'}
        accent={accent}
        right={
          <button
            onClick={onClose}
            className="icon-btn"
            style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0, lineHeight: 0 }}
            aria-label="Dismiss"
          >
            <Icon n="close" s={11} c="var(--ink-dim)" />
          </button>
        }
      />
      <Art
        src={A(artId as Parameters<typeof A>[0], fallbackArtId as Parameters<typeof A>[1])}
        alt={headline}
        scrim="soft"
        style={{ height: 118, flex: 'none', borderBottom: '1px solid var(--line)' }}
      >
        <div style={{ position: 'absolute', left: 12, right: 12, bottom: 10 }}>
          <div
            className="t-display"
            style={{ fontSize: 22, color: 'var(--ink-hi)', letterSpacing: '.05em', textShadow: '0 2px 10px #000' }}
          >
            {headline}
          </div>
        </div>
      </Art>
      <div style={{ padding: '8px 11px 11px' }}>
        <p style={{ margin: '0 0 8px', fontSize: sz(11.5), lineHeight: 1.5, color: 'var(--ink-mid)' }}>{body}</p>
        {rows.map((r) => (
          <CountRow key={r.label} r={r} />
        ))}

        {mastery && (
          <div style={{ marginTop: 9, paddingTop: 8, borderTop: '1px solid var(--line-soft)' }}>
            <Row gap={8} style={{ marginBottom: 5 }}>
              <Icon n="legend" s={13} c={TIER_C[mastery.after]} />
              <span style={{ flex: 1, fontSize: sz(11.5), color: 'var(--ink)' }}>{mastery.stepName}</span>
              {flipped && (
                <span className="t-head" style={{ fontSize: sz(11), color: TIER_C[mastery.after], letterSpacing: '.14em' }}>
                  {mastery.after.toUpperCase()}
                </span>
              )}
            </Row>
            <MasteryBar runs={mastery.runs} from={mastery.from} tier={mastery.after} />
          </div>
        )}

        {items && items.length > 0 && (
          <div style={{ marginTop: 9, paddingTop: 8, borderTop: '1px solid var(--line-soft)' }}>
            {items.map((it, i) => (
              <Row key={`${it.kind}-${it.name}-${i}`} gap={9} style={{ padding: '4px 0' }}>
                {it.artId ? (
                  <Art
                    src={A(it.artId as Parameters<typeof A>[0], 'market.rarity.plate_common')}
                    alt={it.name}
                    scrim="none"
                    style={{ width: 34, height: 34, flex: 'none', border: '1px solid var(--line-soft)' }}
                  />
                ) : (
                  <Icon n="crate" s={16} c={it.kind === 'seized' ? 'var(--arrest)' : 'var(--gold)'} />
                )}
                <span style={{ flex: 1, fontSize: sz(11.5), color: 'var(--ink-hi)', minWidth: 0 }}>{it.name}</span>
                <span
                  className="t-num"
                  style={{ fontSize: sz(12.5), color: it.kind === 'seized' ? 'var(--arrest)' : 'var(--gold)' }}
                >
                  {it.kind === 'seized' ? `taken ×${it.qty}` : `×${it.qty}`}
                </span>
              </Row>
            ))}
          </div>
        )}

        {level && level.after > level.before && (
          <div
            style={{
              marginTop: 9,
              padding: '7px 9px',
              border: '1px solid var(--line-brass)',
              background: 'color-mix(in srgb, var(--gold) 10%, transparent)',
            }}
          >
            <Row gap={8}>
              <Icon n="rep" s={14} c="var(--gold)" />
              <span className="t-head" style={{ fontSize: sz(12), color: 'var(--gold)', letterSpacing: '.1em' }}>
                Level {level.after}
              </span>
              <span style={{ fontSize: sz(11), color: 'var(--ink-mid)', marginLeft: 'auto' }}>
                Energy cap {level.energyMax}
              </span>
            </Row>
          </div>
        )}

        {next && (
          <div style={{ marginTop: 8, fontSize: sz(11.5), color: 'var(--brass-lit)' }}>{next}</div>
        )}
      </div>
    </Panel>
  )
}
